import { 
  REFERRAL_MY_CODE, 
  REFERRAL_CHECK_INVITE_EMAILS,
  REFERRAL_CREATE_INVITE, 
  REFERRAL_MY_INVITES,
  REFERRAL_INVITE_SIGNUPS,
  REFERRAL_INVITE_STATS,
  REFERRAL_UPDATE_MY_INVITE,
  REFERRAL_CHAMPIONS,
  REFERRAL_CHAMPION_MARGIN,
  REFERRAL_MY_MARGIN,
  REFERRAL_MY_REFERRALS,
  REFERRAL_MY_TRACKING_STATS,
  REFERRAL_ADMIN_STATS,
  REFERRAL_CODES,
  REFERRAL_REDEMPTIONS,
  REFERRAL_REFERRALS,
  REFERRAL_INVITES,
  REFERRAL_VALIDATE_CODE,
  REFERRAL_APPLY_CODE,
  ROLES,
  PRICING_PLANS
} from '../../config/Config';
import api from '../../services/api';

/**
 * Get user's referral code and stats
 */
export const getUserReferralCode = async () => {
  try {
    const response = await api.get(REFERRAL_MY_CODE);
    return response.data;
  } catch (error) {
    console.error('Error fetching referral code:', error);
    throw error;
  }
};

/**
 * Check which emails are already active users (for UI: red chip + tooltip)
 */
export const checkInviteEmails = async (emails) => {
  if (!emails || emails.length === 0) return { existing_user_emails: [] };
  try {
    const response = await api.get(REFERRAL_CHECK_INVITE_EMAILS, {
      params: { emails: emails.join(',') },
    });
    return response.data;
  } catch (error) {
    console.error('Error checking invite emails:', error);
    return { existing_user_emails: [] };
  }
};

/**
 * Create a new invite
 */
export const createInvite = async (inviteData) => {
  try {
    const response = await api.post(REFERRAL_CREATE_INVITE, inviteData);
    return response.data;
  } catch (error) {
    console.error('Error creating invite:', error);
    throw error;
  }
};

/**
 * Update an invite
 */
export const updateInvite = async (inviteId, inviteData) => {
  try {
    const response = await api.patch(REFERRAL_UPDATE_MY_INVITE(inviteId), inviteData);
    return response.data;
  } catch (error) {
    console.error('Error updating invite:', error);
    throw error;
  }
};

/**
 * Delete an invite
 */
export const deleteInvite = async (inviteId) => {
  try {
    const response = await api.delete(`${REFERRAL_INVITES}${inviteId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting invite:', error);
    throw error;
  }
};

/**
 * Get user's invites
 */
export const getUserInvites = async () => {
  try {
    const response = await api.get(REFERRAL_MY_INVITES);
    return response.data;
  } catch (error) {
    console.error('Error fetching invites:', error);
    throw error;
  }
};

/**
 * Get signups for an invite (emails that signed up via this link + subscription)
 */
export const getInviteSignups = async (inviteId) => {
  try {
    const response = await api.get(REFERRAL_INVITE_SIGNUPS(inviteId));
    return response.data;
  } catch (error) {
    console.error('Error fetching invite signups:', error);
    throw error;
  }
};

/**
 * Get invite statistics (clicks, unique clicks, registrations, etc.)
 */
export const getInviteStats = async (inviteId) => {
  try {
    const response = await api.get(REFERRAL_INVITE_STATS(inviteId));
    return response.data;
  } catch (error) {
    console.error('Error fetching invite stats:', error);
    throw error;
  }
};

/**
 * Get current user's margin entitlement % (champions only)
 */
export const getMyMargin = async () => {
  try {
    const response = await api.get(REFERRAL_MY_MARGIN);
    return response.data;
  } catch (error) {
    console.error('Error fetching my margin:', error);
    return { margin_percent: null };
  }
};

/**
 * List champions with margin entitlement (admin only)
 */
export const getChampions = async () => {
  try {
    const response = await api.get(REFERRAL_CHAMPIONS);
    return response.data;
  } catch (error) {
    console.error('Error fetching champions:', error);
    throw error;
  }
};

/**
 * Create or update champion margin (admin only). POST = create/update, PATCH = update, DELETE = remove margin
 */
export const setChampionMargin = async (userId, marginPercent) => {
  const response = await api.post(REFERRAL_CHAMPION_MARGIN(userId), { margin_percent: marginPercent });
  return response.data;
};

export const updateChampionMargin = async (userId, marginPercent) => {
  const response = await api.patch(REFERRAL_CHAMPION_MARGIN(userId), { margin_percent: marginPercent });
  return response.data;
};

export const deleteChampionMargin = async (userId) => {
  await api.delete(REFERRAL_CHAMPION_MARGIN(userId));
};

const REFERRAL_PAGE_SIZE = 30;

/**
 * Get user's referrals with pagination.
 * @param {number} days - 7, 30, 90, or 0 for all time
 * @param {number} page - 1-based page number
 * @param {number} pageSize - max records per page (default 30)
 */
export const getUserReferrals = async (days = 0, page = 1, pageSize = REFERRAL_PAGE_SIZE) => {
  try {
    const params = new URLSearchParams();
    if (days > 0) params.set('days', String(days));
    params.set('page', String(Math.max(1, page)));
    params.set('page_size', String(Math.min(100, Math.max(1, pageSize))));
    const url = `${REFERRAL_MY_REFERRALS}${params.toString() ? '?' + params.toString() : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching referrals:', error);
    throw error;
  }
};

/**
 * Get current user's tracking stats (unique clicks, registrations, conversion rate) for the given period.
 * days: 7, 30, 90, or 0 for all time. Default 30.
 */
export const getMyTrackingStats = async (days = 30) => {
  try {
    const response = await api.get(`${REFERRAL_MY_TRACKING_STATS}?days=${days}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching tracking stats:', error);
    return { unique_clicks: 0, registrations: 0, conversion_rate: 0 };
  }
};

/**
 * Get referral statistics (admin only)
 */
export const getReferralStats = async (days = 30) => {
  try {
    const response = await api.get(`${REFERRAL_ADMIN_STATS}?days=${days}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching referral stats:', error);
    throw error;
  }
};

/**
 * Validate a discount/referral code
 */
export const validateCode = async (codeData) => {
  try {
    const response = await api.post(REFERRAL_VALIDATE_CODE, codeData);
    return response.data;
  } catch (error) {
    console.error('Error validating code:', error);
    throw error;
  }
};

/**
 * Apply a code at checkout
 */
export const applyCode = async (codeData) => {
  try {
    const response = await api.post(REFERRAL_APPLY_CODE, codeData);
    return response.data;
  } catch (error) {
    console.error('Error applying code:', error);
    throw error;
  }
};

/**
 * Get all codes (admin only)
 */
export const getAllCodes = async () => {
  try {
    const response = await api.get(REFERRAL_CODES);
    return response.data;
  } catch (error) {
    console.error('Error fetching codes:', error);
    throw error;
  }
};

/**
 * Get all redemptions (admin only)
 */
export const getAllRedemptions = async () => {
  try {
    const response = await api.get(REFERRAL_REDEMPTIONS);
    return response.data;
  } catch (error) {
    console.error('Error fetching redemptions:', error);
    throw error;
  }
};

/**
 * Get all referrals (admin only)
 */
export const getAllReferrals = async () => {
  try {
    const response = await api.get(REFERRAL_REFERRALS);
    return response.data;
  } catch (error) {
    console.error('Error fetching referrals:', error);
    throw error;
  }
};

/**
 * Get all invites (admin only)
 */
export const getAllInvites = async () => {
  try {
    const response = await api.get(REFERRAL_INVITES);
    return response.data;
  } catch (error) {
    console.error('Error fetching invites:', error);
    throw error;
  }
};

/**
 * Create a new code (admin only)
 */
export const createCode = async (codeData) => {
  try {
    const response = await api.post(REFERRAL_CODES, codeData);
    return response.data;
  } catch (error) {
    console.error('Error creating code:', error);
    throw error;
  }
};

/**
 * Update a code (admin only)
 */
export const updateCode = async (codeId, codeData) => {
  try {
    const response = await api.patch(`${REFERRAL_CODES}${codeId}/`, codeData);
    return response.data;
  } catch (error) {
    console.error('Error updating code:', error);
    throw error;
  }
};

/**
 * Delete a code (admin only)
 */
export const deleteCode = async (codeId) => {
  try {
    const response = await api.delete(`${REFERRAL_CODES}${codeId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting code:', error);
    throw error;
  }
};

/**
 * Format referral link for sharing
 */
export const formatReferralLink = (code) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/r/${code}`;
};

/**
 * Format invite link for sharing
 */
export const formatInviteLink = (token) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/invite/${token}`;
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  }
};

/**
 * Format date for display
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Calculate time remaining until expiry
 */
export const getTimeRemaining = (expiryDate) => {
  if (!expiryDate) return 'No expiry';
  
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diff = expiry - now;
  
  if (diff <= 0) return 'Expired';
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `${days}d ${hours}h remaining`;
  if (hours > 0) return `${hours}h ${minutes}m remaining`;
  return `${minutes}m remaining`;
};

/**
 * Get status badge color based on status
 */
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'active':
    case 'valid':
      return 'success';
    case 'expired':
    case 'inactive':
      return 'error';
    case 'pending':
    case 'awaiting':
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * Format discount amount for display
 */
export const formatDiscountAmount = (amount, type) => {
  if (!amount) return 'N/A';
  
  if (type === 'PERCENT') {
    return `${amount}% off`;
  } else if (type === 'FIXED') {
    return `$${parseFloat(amount).toFixed(2)} off`;
  }
  
  return amount;
};

/**
 * Generate referral code display name
 */
export const getReferralCodeDisplay = (code) => {
  if (!code) return 'N/A';
  return code.toUpperCase();
};

/**
 * Check if user can create invites
 */
export const canCreateInvites = (user) => {
  // For now, allow all authenticated users
  // This can be customized based on role or subscription
  return user && user.id;
};

/**
 * Check if user can view admin stats
 */
export const canViewAdminStats = (user) => {
  return user && (user.is_staff || user.is_superuser);
};

/**
 * Fetch paginated data from an endpoint
 */
const fetchPaginated = async (endpoint) => {
  let url = endpoint;
  let all = [];
  while (url) {
    const res = await api.get(url);
    const data = res.data;
    if (Array.isArray(data)) {
      all = data; // non-paginated
      break;
    }
    all = all.concat(data.results || []);
    url = data.next || null;
  }
  return all;
};

/**
 * Get all roles for dropdown selection
 */
export const getAllRoles = async () => {
  try {
    const roles = await fetchPaginated(ROLES);
    return { roles };
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw error;
  }
};

/**
 * Get all pricing plans for dropdown selection
 */
export const getAllPricingPlans = async () => {
  try {
    const plans = await fetchPaginated(PRICING_PLANS);
    return { plans };
  } catch (error) {
    console.error('Error fetching pricing plans:', error);
    throw error;
  }
};
